// @flow

import React, { useCallback, useMemo, Fragment } from 'react';
import prop from 'lodash/fp/prop';
import find from 'lodash/fp/find';
import propEq from 'lodash/fp/propEq';
import connect from '@active/awe-ui-core/lib/hoc/connect';
import NoRecord from 'components/NoRecord/NoRecord';
import Countdown from 'components/VirtualEvent/Countdown/index';
import SubmitResult from 'components/VirtualEvent/SubmitResult/index';

import type {
  ParticipantSearchResult,
  SubmitVirtualEventResultPayload,
  VirtualEventStageResult,
} from 'type/type';

import {
  getEvent,
  getResultType,
  getParticipantSearchResult,
  isSearchingParticipantByNumber,
  isSubmittingVirtualEventResult as isSubmittingVirtualEventResultSelector,
  isVirtualEventResultSubmitted as isVirtualEventResultSubmittedSelector,
} from '../../duck/selector';
import {
  changeResultType as changeResultTypeAction,
  searchParticipantByNumber as searchParticipantByNumberAction,
  submitVirtualEventResult as submitVirtualEventResultAction,
} from '../../duck/duck';
import Dashboard from '../Dashboard/Dashboard';

type Props = {
  noRecord: boolean,
  eventId: string,
  startDate: string,
  endDate: string,
  externalId: string,
  searchParticipantByNumber: Function,
  participantSearchResult: ParticipantSearchResult,
  isSearching: boolean,
  isSubmittingVirtualEventResult: boolean,
  isVirtualEventResultSubmitted: boolean,
  submitVirtualEventResult: (payload: SubmitVirtualEventResultPayload) => void,
  resultType: string,
  changeResultType: Function,
}

export const VirtualRace = ({
  eventId,
  startDate,
  endDate,
  noRecord = true,
  externalId,
  searchParticipantByNumber,
  participantSearchResult,
  isSearching,
  isSubmittingVirtualEventResult,
  isVirtualEventResultSubmitted,
  submitVirtualEventResult,
  resultType,
  changeResultType,
}: Props) => {
  const onSearchParticipantByNumber = useCallback((
    { registrationNumber }: { registrationNumber: string },
  ) => {
    searchParticipantByNumber({ eventId, registrationNumber, resubmit: false });
  }, [ eventId, searchParticipantByNumber ]);

  const onSubmitVirtualEventResult = useCallback(({
    stageResults,
    fileId,
  }: {
    stageResults: Array<VirtualEventStageResult>,
    fileId: string,
  }) => {
    submitVirtualEventResult({
      stageResults,
      eventId,
      externalRouteId: prop('externalRouteId')(participantSearchResult),
      registrationNumber: prop('registrationNumber')(participantSearchResult),
      uploadFileId: fileId,
      resubmit: false,
    });
  }, [ submitVirtualEventResult, eventId, participantSearchResult ]);

  const isVirtualEventNotStart = useMemo(() => {
    const currentTime = (new Date()).getTime();
    return currentTime < new Date(startDate).getTime();
  }, [ startDate ]);

  const isVirtualEventOngoing = useMemo(() => {
    const currentTime = (new Date()).getTime();
    const startTime = new Date(startDate).getTime();
    const endTime = new Date(endDate).getTime();
    return currentTime >= startTime && currentTime <= endTime;
  }, [ startDate, endDate ]);

  const isVirtualEventGone = useMemo(() => {
    const currentTime = (new Date()).getTime();
    return currentTime > new Date(endDate).getTime();
  }, [ endDate ]);

  const handleResultTypeChange = useCallback((newType: string) => {
    if (resultType !== newType) {
      changeResultType(newType);
    }
  }, [ resultType, changeResultType ]);

  if (noRecord && isVirtualEventGone) {
    return (
      <div className="page">
        <NoRecord />
      </div>
    );
  }

  return (
    <Fragment>
      {!isVirtualEventGone && (
        <div className="virtual-event">
          {isVirtualEventNotStart && (
            <Countdown startDate={startDate} />
          )}
          {isVirtualEventOngoing && (
            <SubmitResult
              endDate={endDate}
              eventExternalId={externalId}
              onSearchParticipant={onSearchParticipantByNumber}
              participantInfo={participantSearchResult}
              isSearching={isSearching}
              isSubmitting={isSubmittingVirtualEventResult}
              resultSubmitted={isVirtualEventResultSubmitted}
              onSubmitResult={onSubmitVirtualEventResult} />
          )}
        </div>
      )}
      {!noRecord && !participantSearchResult && (
        <Dashboard
          className={!isVirtualEventGone ? 'container' : ''}
          resultType={resultType}
          onChangeResultType={handleResultTypeChange} />
      )}
    </Fragment>
  );
};

const mapState = (state) => {
  const event = getEvent(state);

  if (!event) {
    return ({
      noRecord: true,
    });
  }

  const {
    startDate,
    endDate,
    isVirtual,
    externalId,
    id,
    routes,
  } = event.toJSON();

  return ({
    eventId: id,
    noRecord: !find(propEq('virtual', true))(routes),
    resultType: getResultType(state),
    startDate,
    endDate,
    isVirtual,
    externalId,
    participantSearchResult: getParticipantSearchResult(state),
    isSearching: isSearchingParticipantByNumber(state),
    isSubmittingVirtualEventResult: isSubmittingVirtualEventResultSelector(state),
    isVirtualEventResultSubmitted: isVirtualEventResultSubmittedSelector(state),
  });
};

const VirtualRaceContainer = connect(mapState, {
  changeResultType: changeResultTypeAction,
  searchParticipantByNumber: searchParticipantByNumberAction,
  submitVirtualEventResult: submitVirtualEventResultAction,
})(VirtualRace);

export default VirtualRaceContainer;
